import { z } from 'zod/v4';

// ============================================
// Multi-Currency Prices
// ============================================

/**
 * Схема мультивалютных цен
 * Ключ - код валюты (3 символа), значение - цена
 */
export const multiCurrencyPricesSchema = z.record(
  z.string().min(3).max(3),
  z.number().positive(),
).nullable().optional();

export type MultiCurrencyPrices = z.infer<typeof multiCurrencyPricesSchema>;

// ============================================
// Pagination
// ============================================

/**
 * Базовая схема пагинации
 */
export const paginationSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(20),
});

export type PaginationInput = z.infer<typeof paginationSchema>;

/**
 * Расширенная схема пагинации с сортировкой
 */
export const paginationWithSortSchema = paginationSchema.extend({
  sortBy: z.string().optional(),
  sortOrder: z.enum(['asc', 'desc']).optional(),
});

export type PaginationWithSortInput = z.infer<typeof paginationWithSortSchema>;

// ============================================
// Translations
// ============================================

/**
 * Базовая схема перевода
 */
export const baseTranslationSchema = z.object({
  languageCode: z.string().min(2).max(5),
});

/**
 * Схема перевода с названием
 */
export const namedTranslationSchema = baseTranslationSchema.extend({
  name: z.string().min(1).max(200),
  description: z.string().max(5000).nullable().optional(),
});

export type NamedTranslation = z.infer<typeof namedTranslationSchema>;

/**
 * Схема перевода с заголовком (для курсов, статей)
 */
export const titledTranslationSchema = baseTranslationSchema.extend({
  title: z.string().min(1).max(200),
  description: z.string().max(5000).nullable().optional(),
  shortDescription: z.string().max(500).nullable().optional(),
});

export type TitledTranslation = z.infer<typeof titledTranslationSchema>;

/**
 * Схема перевода с контентом (для статей, уроков)
 */
export const contentTranslationSchema = baseTranslationSchema.extend({
  title: z.string().min(1).max(200),
  content: z.string().max(50000).nullable().optional(),
});

export type ContentTranslation = z.infer<typeof contentTranslationSchema>;

// ============================================
// Common Fields
// ============================================

/**
 * Схема для slug (URL-friendly идентификатор)
 */
export const slugSchema = z
  .string()
  .min(3, 'Slug должен быть минимум 3 символа')
  .max(100)
  .regex(/^[a-z0-9-]+$/, 'Slug может содержать только строчные буквы, цифры и дефисы');

/**
 * Схема для email
 */
export const emailSchema = z.string().email('Некорректный email');

/**
 * Схема для URL
 */
export const urlSchema = z.string().url('Некорректный URL');

/**
 * Схема для телефона
 */
export const phoneSchema = z.string().regex(/^\+?[1-9]\d{1,14}$/, 'Некорректный номер телефона');

/**
 * Схема для кода валюты (ISO 4217)
 */
export const currencyCodeSchema = z.string().min(3).max(3).toUpperCase();

/**
 * Схема для положительного числа (цена)
 */
export const priceSchema = z.number().positive('Цена должна быть положительной');

/**
 * Схема для ID (положительное целое)
 */
export const idSchema = z.coerce.number().int().positive();

/**
 * Схема для опционального ID
 */
export const optionalIdSchema = z.coerce.number().int().positive().optional();

// ============================================
// Date/Time
// ============================================

/**
 * Схема для ISO datetime строки
 */
export const isoDateTimeSchema = z.string().datetime();

/**
 * Схема для опционального datetime
 */
export const optionalIsoDateTimeSchema = z.string().datetime().nullable().optional();

/**
 * Схема для ISO date строки (без времени)
 */
export const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Формат даты: YYYY-MM-DD');

// ============================================
// Utilities
// ============================================

/**
 * Преобразование строки в boolean для query параметров
 */
export const stringToBooleanSchema = z.preprocess((val) => {
  if (typeof val === 'boolean') {
    return val;
  }
  if (val === 'true') {
    return true;
  }
  if (val === 'false') {
    return false;
  }
  return;
}, z.boolean().optional());

/**
 * Преобразование строки в число для query параметров
 */
export const stringToNumberSchema = z.preprocess((val) => {
  if (typeof val === 'number') {
    return val;
  }
  if (typeof val === 'string') {
    const parsed = Number(val);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }
  return;
}, z.number().optional());
